import { Link } from "react-router-dom";
import { getCategory } from "@/mocks/categories";
import type { Article } from "@/mocks/articles";

const colorMap: Record<string, string> = {
  primary: "bg-primary-100 text-primary-700",
  accent: "bg-accent-100 text-accent-900",
  secondary: "bg-secondary-100 text-secondary-900",
};

export function CategoryBadge({ slug }: { slug: string }) {
  const category = getCategory(slug);
  if (!category) return null;
  return (
    <Link
      to={`/category/${category.slug}`}
      className={`cursor-pointer whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${
        colorMap[category.color] ?? colorMap.secondary
      }`}
    >
      {category.name}
    </Link>
  );
}

type CardProps = {
  article: Article;
  variant?: "default" | "wide" | "compact";
  index?: number;
};

export default function ArticleCard({ article, variant = "default", index }: CardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex cursor-pointer items-start gap-3 border-b border-background-200/80 py-3 last:border-b-0"
      >
        {typeof index === "number" && (
          <span className="mt-0.5 font-heading text-lg font-black text-background-400">
            {String(index + 1).padStart(2, "0")}
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium leading-snug text-foreground-900 group-hover:text-primary-600 line-clamp-2">
            {article.title}
          </span>
          <span className="mt-1.5 flex items-center gap-2 text-xs text-secondary-600">
            <span>{article.date}</span>
            <span className="h-1 w-1 rounded-full bg-background-400" />
            <span>{article.views} 阅读</span>
          </span>
        </span>
      </Link>
    );
  }

  if (variant === "wide") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group grid cursor-pointer grid-cols-1 overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300 sm:grid-cols-5"
      >
        <div className="relative h-52 w-full overflow-hidden sm:col-span-2 sm:h-full">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 爆料在线`}
            className="h-full w-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex flex-col p-5 sm:col-span-3">
          <div className="flex items-center gap-2">
            <CategoryBadge slug={article.category} />
            <span className="text-xs text-secondary-600">{article.date}</span>
          </div>
          <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950 group-hover:text-primary-600">
            {article.title}
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-foreground-600 line-clamp-3">
            {article.excerpt}
          </p>
          <div className="mt-auto flex items-center gap-3 pt-4 text-xs text-secondary-600">
            <span className="flex items-center gap-1">
              <i className="ri-user-3-line" aria-hidden="true" />
              {article.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line" aria-hidden="true" />
              {article.readMinutes} 分钟
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/article/${article.id}`} className="flex h-full cursor-pointer flex-col">
        <div className="relative h-48 w-full overflow-hidden">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 爆料在线`}
            className="h-full w-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <span className="absolute left-3 top-3">
            <CategoryBadge slug={article.category} />
          </span>
        </div>
        <div className="flex flex-1 flex-col p-5">
          <h3 className="font-heading text-base font-bold leading-snug text-foreground-950 group-hover:text-primary-600 line-clamp-2">
            {article.title}
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-foreground-600 line-clamp-3">
            {article.excerpt}
          </p>
          <div className="mt-auto flex items-center justify-between pt-4 text-xs text-secondary-600">
            <span className="flex items-center gap-1">
              <i className="ri-user-3-line" aria-hidden="true" />
              {article.author}
            </span>
            <span>{article.date}</span>
          </div>
        </div>
      </Link>
    </article>
  );
}